import * as React from "react";
import "./truckLoader.css";

/*
 * The Mewahtrans Web System's loading rig (Mewahtrans_Web_System/src/common/Loader.jsx),
 * ported so a long report run on the GPS system reads as "working" instead of an
 * empty page. Same drawing, same Mewahtrans blue paint and the same motion; the
 * Web System's per-brand repaint is left out because this copy is only shown on
 * the Mewahtrans GPS build (see reports.js).
 *
 * Plain inline styles rather than the Web System's Tailwind classes — this app
 * has no Tailwind.
 */
const PAINT = {
	roof: "#38bdf8",
	roofCrease: "#0369a1",
	cab: "#0284c7",
	doorLine: "#0369a1",
	grille: "#ffffff",
	boxBody: "#64748b",
	boxTopRail: "#475569",
	boxBottomRail: "#334155",
	boxRib: "#475569",
	boxHighlight: "#94a3b8",
	boxCorner: "#334155",
	boxCornerEdge: "#1e293b",
	trailerDeck: "#1e293b",
	trailerBar: "#334155",
	trailerFender: "#1e293b",
	trailerGuard: "#94a3b8",
	trailerGuardPost: "#475569",
	trailerLeg: "#475569",
	trailerLegFoot: "#334155",
};

const BLUE = "#0284c7";

const TruckCab = () => (
	<g>
		<path d="M190 26 V15 Q190 11 194 12 V25" fill="#334155" />
		<path d="M193 15 Q212 16 234 25 L241 29 H192 Z" fill={PAINT.roof} />
		<path d="M192 27 Q215 23 239 27 Q245 28 245.5 33 L247 57 L246 64 L243 65 L241 61 Q232 53 223 64 H194 Q190 64 190 59 V34 Q190 29 192 27Z" fill={PAINT.cab} stroke={PAINT.doorLine} strokeWidth="0.6" />
		<path d="M204 27 Q201 30 201 36 V57 Q201 61 206 61 H219 M191 36 L200 35 M191 53 L199 54" stroke={PAINT.doorLine} strokeWidth="0.6" />
		<path d="M205 33 Q219 31 233 32 L235 46 L218 45 Q208 44 205 40Z" fill="#1e293b" />
		<path d="M235 32 L244 33 L245 46 H237Z" fill="#0f172a" />
		<path d="M207 33 L232 32.6 M238 33.5 L243 34" stroke="#94a3b8" strokeWidth="0.6" opacity="0.6" />
		<path d="M215 33 V44 M234 32 L236 46" stroke={PAINT.cab} strokeWidth="1.7" />
		<path d="M205 31 Q226 29.5 244.5 31.5 L245 33 L234 32 L205 33Z" fill={PAINT.roofCrease} />
		<path d="M226 33 L231 32 L233 44 L230 45" stroke="#334155" strokeWidth="1" />
		<rect x="227" y="33" width="4.3" height="4.4" rx="0.8" fill={PAINT.cab} stroke={PAINT.doorLine} strokeWidth="0.5" />
		<rect x="228" y="38" width="4.8" height="7" rx="0.9" fill={PAINT.cab} stroke={PAINT.doorLine} strokeWidth="0.5" />
		<rect x="211" y="46" width="3.5" height="1" rx="0.4" fill="#f8fafc" />
		<path d="M220 65 Q222 55 232 55 Q241 55 245 62" stroke="#1e293b" strokeWidth="2.3" />
		<path d="M241 49 L247 50 V64 L243 65 L242 60Z" fill="#1e293b" />
		<path d="M242 51 L246 51.7 M242.5 54 L246.5 54.5 M243 57 L246.5 57.4" stroke={PAINT.grille} strokeWidth="0.8" />
		<path d="M242.5 59 L246.5 59.5 V61.2 L243 61Z" fill="#e4f3e6" />
		<rect x="246" y="59.5" width="1" height="1.7" fill="#ffc343" />
		<path d="M208 59 H218 M208 62 H217" stroke="#334155" strokeWidth="1.4" />
		<rect x="209" y="54" width="3" height="3" rx="0.5" fill={PAINT.doorLine} />
	</g>
);

export default function TruckLoader({ label = "Generating report…" }) {
	return (
		<div className="gps-truck-loader" role="status" aria-live="polite">
			<svg className="gps-truck-loader__svg" viewBox="0 0 260 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
				{/* Route rail, lit behind the rig */}
				<line x1="12" y1="78.5" x2="248" y2="78.5" stroke="#E2E8F0" strokeWidth="3" strokeLinecap="round" />
				<line className="gps-truck-loader__rail" x1="12" y1="78.5" x2="248" y2="78.5" stroke={BLUE} strokeWidth="3" strokeLinecap="round" />

				{/* Stage stops, lit one after another as the rig passes */}
				{[12, 90, 168, 248].map((cx) => (
					<circle
						key={cx}
						className="gps-truck-loader__stop"
						style={{ animationDelay: `${Math.max(0, ((cx - 12) / 236) * 2.6 - 0.42).toFixed(2)}s` }}
						cx={cx} cy="78.5" r="4.5"
						fill="#fff" stroke={BLUE} strokeWidth="2.5"
					/>
				))}

				<g className="gps-truck-loader__truck">
					{/* Speed streaks */}
					<g className="gps-truck-loader__streaks" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" opacity="0.45">
						<line x1="6" y1="20" x2="24" y2="20" />
						<line x1="12" y1="32" x2="30" y2="32" />
						<line x1="6" y1="44" x2="20" y2="44" />
					</g>

					{/* 40ft container */}
					<g>
						<rect x="26" y="14" width="158" height="38" rx="1.5" fill={PAINT.boxBody} />
						<rect x="26" y="14" width="158" height="3" fill={PAINT.boxTopRail} />
						<rect x="26" y="49" width="158" height="3" fill={PAINT.boxBottomRail} />
						<line x1="28" y1="15" x2="182" y2="15" stroke={PAINT.boxHighlight} strokeWidth="0.8" opacity="0.6" />
						<g stroke={PAINT.boxRib} strokeWidth="1.6" opacity="0.9">
							{[35, 44, 53, 62, 71, 80, 89, 98, 107, 116, 125, 134, 143, 152, 161, 170, 179].map((x) => (
								<line key={x} x1={x} y1="17" x2={x} y2="49" />
							))}
						</g>
						{[[27, 15], [179, 15], [27, 47], [179, 47]].map(([x, y]) => (
							<rect key={`${x}-${y}`} x={x} y={y} width="4.5" height="4" rx="0.8" fill={PAINT.boxCorner} stroke={PAINT.boxCornerEdge} strokeWidth="0.5" />
						))}
					</g>

					{/* Skeletal trailer chassis */}
					<g>
						<rect x="24" y="52" width="166" height="4" rx="1" fill={PAINT.trailerDeck} />
						<path d="M24 54 v11 h3 v-11 z" fill={PAINT.trailerBar} />
						<rect x="24.5" y="59" width="2" height="4.5" rx="0.5" fill="#ef4444" />
						<path d="M34 65 q 4 -8 12 -8 h 36 q 7 0 12 8" fill="none" stroke={PAINT.trailerFender} strokeWidth="2.8" strokeLinecap="round" />
						<g stroke={PAINT.trailerGuard} strokeWidth="1.4" strokeLinecap="round">
							<line x1="102" y1="58.5" x2="148" y2="58.5" />
							<line x1="102" y1="62.5" x2="148" y2="62.5" />
							<line x1="108" y1="55" x2="108" y2="64" stroke={PAINT.trailerGuardPost} strokeWidth="1.8" />
							<line x1="142" y1="55" x2="142" y2="64" stroke={PAINT.trailerGuardPost} strokeWidth="1.8" />
						</g>
						<rect x="94" y="55" width="3" height="9" fill={PAINT.trailerLeg} />
						<rect x="91" y="63" width="9" height="2" rx="0.8" fill={PAINT.trailerLegFoot} />
					</g>

					{/* Prime mover */}
					<g>
						<rect x="160" y="52" width="84" height="4" rx="1" fill="#1e293b" />
						<rect x="166" y="49" width="16" height="3" rx="1" fill="#334155" />
						<rect x="190" y="14" width="3.2" height="38" rx="1.5" fill="#94a3b8" />
						<path d="M190 14 q 0 -3 3 -3 h 1.5 v 2.5 h -2.5 q -2 0 -2 0.5 z" fill="#64748b" />
						<rect x="189.5" y="24" width="4.2" height="2" rx="0.5" fill="#334155" />
						<rect x="198" y="55" width="11" height="11" rx="1.5" fill="#1e293b" />
						<line x1="200" y1="58" x2="207" y2="58" stroke="#334155" strokeWidth="1" />
						<line x1="200" y1="61" x2="207" y2="61" stroke="#334155" strokeWidth="1" />
						<line x1="200" y1="64" x2="207" y2="64" stroke="#334155" strokeWidth="1" />
						<rect x="211" y="55" width="18" height="11" rx="4.5" fill="#cbd5e1" stroke="#64748b" strokeWidth="0.8" />
						<line x1="215" y1="55" x2="215" y2="66" stroke="#334155" strokeWidth="1.2" />
						<line x1="225" y1="55" x2="225" y2="66" stroke="#334155" strokeWidth="1.2" />
						<path d="M160 65 q 4 -7.5 10 -7.5 h 5 q 4 0 7.5 7.5" fill="none" stroke="#1e293b" strokeWidth="2.8" />
						<path d="M180 65 q 4 -7.5 10 -7.5 h 5 q 4 0 7.5 7.5" fill="none" stroke="#1e293b" strokeWidth="2.8" />
						<TruckCab />
					</g>

					{/* Wheels: tri-axle trailer + tandem drive + steer, all resting on the rail */}
					{[42, 62, 82, 168, 188, 232].map((cx) => (
						<g key={cx}>
							<circle cx={cx} cy="69.5" r="9" fill="#1e293b" stroke="#0f172a" strokeWidth="0.6" />
							<circle cx={cx} cy="69.5" r="8" fill="#111827" />
							<circle cx={cx} cy="69.5" r="5.8" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="0.6" />
							<g className="gps-truck-loader__hub" style={{ transformOrigin: `${cx}px 69.5px` }}>
								<circle cx={cx} cy="69.5" r="3.2" fill="#334155" />
								{[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
									const rad = (deg * Math.PI) / 180;
									return <circle key={deg} cx={cx + Math.cos(rad) * 4.4} cy={69.5 + Math.sin(rad) * 4.4} r="0.55" fill="#1e293b" />;
								})}
								<circle cx={cx} cy="69.5" r="1.6" fill="#475569" />
								<circle cx={cx} cy="69.5" r="0.8" fill="#cbd5e1" />
							</g>
						</g>
					))}
				</g>
			</svg>
			<p className="gps-truck-loader__label">{label}</p>
		</div>
	);
}
